/**
 * web/src/screens/Notebook/giftflow/step4.tsx — Step 4, Plan: the pick, the
 * checklist by source type with Find / Add / Draft, the STATUS control
 * (To book / Booked / Given), and the one explicit disclosure in the whole
 * flow — Reveal to {partner} on {date} vs Keep it private (card
 * NYJEJRCBfBGQ). No visibility toggle anywhere else: everything in the flow
 * is giver-private until this button is pressed.
 */
import type { GiftFlowState } from './hooks';
import { PLAN_STATUS_LABELS, fmtDate, fmtDeadlineLine, planStepMeta } from './types';

export function StepPlan({ g }: { g: GiftFlowState }) {
  const partner = g.partnerName;
  const picked = g.recs.find((r) => r.state === 'picked') ?? null;
  const plan = picked ? g.plans.find((p) => p.recId === picked.recId) ?? null : null;
  const search = g.active;
  const revealDate = search?.occasionDate ?? picked?.deadline ?? null;

  if (!picked) {
    return (
      <section className="gf-step" data-testid="gift-step-4">
        <h4 className="gf-heading">Plan</h4>
        <p className="gf-muted" data-testid="gift-plan-empty">
          Pick an idea on the previous step and the plan appears here.
        </p>
      </section>
    );
  }

  return (
    <section className="gf-step" data-testid="gift-step-4">
      <p className="gf-label">You picked</p>
      <div className="gf-plan-pick" data-testid="gift-plan-pick">
        <span className="gf-idea-title" data-testid="gift-plan-pick-title">{picked.title}</span>
        <span className="gf-idea-price">
          {picked.price !== null ? `A$${picked.price}` : picked.priceEstimate ? `~${picked.priceEstimate}` : ''}
        </span>
        <span className="gf-muted" data-testid="gift-plan-occasion">
          {search?.occasionType ?? ''}
          {search?.occasionDate ? ` · ${fmtDate(search.occasionDate)}` : ''}
        </span>
      </div>

      {plan && (
        <>
          <ul className="gf-plan-steps" data-testid="gift-plan-steps">
            {plan.steps.map((s, i) => {
              const meta = planStepMeta(s.label, picked.deadline);
              return (
                <li key={`${s.label}-${i}`} className="gf-plan-step" data-testid={`gift-plan-step-${i}`}>
                  <div className="gf-plan-step-main">
                    <span className={s.done ? 'gf-plan-step-done' : ''}>{s.label}</span>
                    <span className="gf-muted">{meta.sub}</span>
                  </div>
                  <button
                    type="button"
                    className={`pear-btn ${s.done ? 'pear-btn-quiet' : 'pear-btn-quiet'}`}
                    data-testid={`gift-plan-action-${i}`}
                    disabled={g.busy}
                    onClick={() => void g.togglePlanStep(plan, i)}
                  >
                    {s.done ? 'Done' : meta.action}
                  </button>
                </li>
              );
            })}
          </ul>

          <p className="gf-label">Status</p>
          <div className="gf-chips" role="group" aria-label="Status" data-testid="gift-plan-status">
            {PLAN_STATUS_LABELS.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`gf-chip${plan.status === s.value ? ' is-on' : ''}`}
                data-testid={`gift-status-${s.value}`}
                aria-pressed={plan.status === s.value}
                disabled={g.busy}
                onClick={() => void g.setPlanStatus(plan, s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>

          <p className="gf-muted" data-testid="gift-plan-note">
            {partner} sees nothing until you reveal it. Want Pear to share it on the day?
          </p>
          <p className="gf-muted" data-testid="gift-plan-reveal-state">
            {plan.revealMode === 'on_date' && plan.revealDate
              ? `Reveal set for ${fmtDate(plan.revealDate)}.`
              : 'Kept private.'}
          </p>
          <div className="gf-plan-actions">
            <button
              type="button"
              className="pear-btn pear-btn-mustard"
              data-testid="gift-reveal"
              disabled={g.busy || !revealDate}
              title={revealDate ? undefined : 'This occasion has no date to reveal on'}
              onClick={() => void g.setReveal(plan, 'on_date', revealDate)}
            >
              Reveal to {partner} on {revealDate ? fmtDate(revealDate) : 'the day'}
            </button>
            <button
              type="button"
              className="pear-btn pear-btn-quiet"
              data-testid="gift-keep-private"
              disabled={g.busy}
              onClick={() => void g.setReveal(plan, 'private', null)}
            >
              Keep it private
            </button>
          </div>
        </>
      )}
      {!plan && (
        <p className="gf-muted" data-testid="gift-plan-missing">
          The plan for this pick is being written{fixDeadline(picked.deadline)}.
        </p>
      )}
    </section>
  );
}

function fixDeadline(deadline: string | null): string {
  const line = fmtDeadlineLine(deadline);
  return line ? ` — ${line}` : '';
}
